import React, { useContext, useEffect, useState, useRef } from 'react';
import { StyleSheet, TextInput, View } from 'react-native';
import { Tab, Dialog, Divider, useTheme } from '@rneui/themed';
import dayjs from 'dayjs';
import { MaterialCommunityIcons, FontAwesome } from '@expo/vector-icons';
import ConsumptionContext from '../consumption-context';
import { StdUnitsInput } from './StdUnitsInput';
import { useTranslation } from 'react-i18next';

const EditUnitDialog = ({
  date,
  visible,
  onOkPress,
  onConfirmZeroPress,
  addConsumption,
  consumptionGoal,
  dailyComment,
  addOrUpdateDailyComment,
  totalByUnitForDay,
  zeroConfirmed,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { consumption, total: totalConsumption } =
    useContext(ConsumptionContext);
  const [tabIndex, setTabIndex] = useState(0);
  const [tabContentLayout, setTabContentLayout] = useState({});
  const isDailyCommentChanged = useRef(false);
  const [text, setText] = useState(dailyComment || '');
  const d = dayjs(date);

  useEffect(() => {
    if (visible) {
      setTabIndex(0);
      setText(dailyComment || '');
      isDailyCommentChanged.current = false;
    }
  }, [visible]);

  const changeDailyComment = (comment) => {
    isDailyCommentChanged.current = true;
    setText(comment);
  };

  const pressOk = () => {
    if (text && isDailyCommentChanged.current) {
      addOrUpdateDailyComment(text);
    }
    onOkPress();
  };

  return (
    <Dialog
      overlayStyle={styles.overlayStyle}
      isVisible={visible}
      onBackdropPress={pressOk}
    >
      <Dialog.Title title={`${d.format('dddd')} ${d.format('YYYY-MM-DD')}`} />
      <Divider />

      <Tab
        style={{ marginTop: 20 }}
        value={tabIndex}
        onChange={(e) => setTabIndex(e)}
        indicatorStyle={{
          backgroundColor: theme.colors.primary,
          height: 3,
        }}
      >
        <Tab.Item
          title={t('log.editDialog.standardUnits')}
          titleStyle={{ fontSize: 12, color: theme.colors.primary }}
          icon={
            <MaterialCommunityIcons
              name="glass-wine"
              size={26}
              color={theme.colors.primary}
            />
          }
        />
        <Tab.Item
          title={t('log.editDialog.thoughts')}
          titleStyle={{ fontSize: 12, color: theme.colors.primary }}
          icon={
            <FontAwesome
              name="pencil-square-o"
              size={26}
              color={theme.colors.primary}
            />
          }
        />
      </Tab>

      <View onLayout={(event) => setTabContentLayout(event.nativeEvent.layout)}>
        {tabIndex === 0 ? (
          <View style={styles.stdUnitsInput}>
            <StdUnitsInput
              addConsumption={addConsumption}
              consumptionGoal={consumptionGoal}
              consumptionTodayByUnit={totalByUnitForDay(consumption, d)}
              date={date}
              totalConsumption={totalConsumption}
              zeroConfirmed={zeroConfirmed}
            />
          </View>
        ) : (
          <View
            style={{
              height: tabContentLayout.height,
              width: tabContentLayout.width,
            }}
          >
            <TextInput
              style={styles.textInput}
              multiline={true}
              onChangeText={
                text.length < 1024
                  ? (t) => changeDailyComment(t)
                  : (t) => changeDailyComment(t.substring(0, t.length - 2))
              }
              value={text}
              placeholder={t('log.editDialog.dailyThoughts')}
              placeholderTextColor="grey"
            />
          </View>
        )}
      </View>

      <Dialog.Actions>
        <Dialog.Button type="solid" title="Klar" onPress={pressOk} />
        {!zeroConfirmed && totalConsumption.daily[date] === 0 && (
          <Dialog.Button
            type="solid"
            buttonStyle={{ backgroundColor: 'green' }}
            title={t('log.editDialog.confirmZero')}
            onPress={() => {
              onConfirmZeroPress();
              pressOk();
            }}
          />
        )}
      </Dialog.Actions>
    </Dialog>
  );
};

const styles = StyleSheet.create({
  overlayStyle: {
    backgroundColor: 'white',
    width: '95%',
  },
  stdUnitsInput: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 0,
    paddingTop: 20,
    borderColor: 'lightgrey',
    borderRadius: 3,
    borderWidth: 0,
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: 'grey',
    padding: 10,
    marginTop: 35,
    borderRadius: 3,
    fontSize: 18,
  },
});

export default EditUnitDialog;
